import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import BASE_URL from '../../url';
import Swal from 'sweetalert2';
import AddModal from '../../Resume_Submission/Components/AddModal';
import {
  CheckCircle, Clock, Search, UserCheck, Briefcase, FileCheck,
  Send, Sparkles, Building2, Calendar, ShieldCheck, Mail,
  AlertCircle, RefreshCw, LogOut, User, Phone, MapPin, Globe,
  FileText, Download, Eye, ExternalLink, GraduationCap, Award,
  CheckCircle2, DollarSign, ArrowRight, UserCheck2, ChevronRight,
  Edit, X, XCircle, AlertTriangle, Layers, Home, RotateCcw
} from 'lucide-react';
import '../styles/CandidateStatusTracker.css';
import prophecyLogo from "../../Recruitment/Assets/images/prophecy-logo.png";
import prophecyLogo2 from "../../Recruitment/Assets/images/prophecy-logo2.png";
import careersHeroBg from "../../Recruitment/Assets/images/careers-hero-bg.png";

export default function CandidateStatusTracker({ isEmbedded = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  const loggedInUser = localStorage.getItem('username') || '';
  const loggedInRole = (localStorage.getItem('role') || '').toLowerCase().trim();
  const isCandidateUser = loggedInRole === 'external user' || loggedInRole === 'external_user' || loggedInRole === 'external-user' || loggedInRole === 'candidate';
  const isLoggedIn = !!localStorage.getItem('token');
  const isInternal = isEmbedded || (isLoggedIn && !isCandidateUser) || location.pathname === '/profile';

  let userObj = {};
  try {
    userObj = JSON.parse(localStorage.getItem('user') || '{}');
  } catch (e) {
    userObj = {};
  }

  const candidateCodeStored = (localStorage.getItem('candidateCode') || userObj.candidateCode || '').trim();
  const candidateIdStored = (localStorage.getItem('candidateId') || userObj.candidateId || '').trim();
  const candidateCodeUser = (localStorage.getItem('candidateCodeUser') || '').toLowerCase().trim();
  const currentCandidateEmail = (userObj.email || loggedInUser || '').toLowerCase().trim();

  // If a candidate user is logged in, ensure cached candidateCode matches their current session
  const isCodeMatchingUser = !candidateCodeUser || !currentCandidateEmail || candidateCodeUser === currentCandidateEmail;
  const validCandidateCodeStored = isCodeMatchingUser ? candidateCodeStored : '';
  const validCandidateIdStored = isCodeMatchingUser ? candidateIdStored : '';

  const urlParamQuery = (queryParams.get('q') || queryParams.get('id') || queryParams.get('email') || '').trim();

  // ONLY auto-populate query if explicitly provided via URL param or stored candidate credentials
  // Never auto-populate admin/staff email or username into the tracking input!
  const initialQuery = urlParamQuery || 
    (isCandidateUser ? (validCandidateCodeStored || validCandidateIdStored || userObj.email || loggedInUser) : (candidateCodeStored || candidateIdStored));

  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusData, setStatusData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState(null);

  const fetchStatus = async (identifier) => {
    if (!identifier || !identifier.trim()) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError('');

      const res = await fetch(`${BASE_URL}/api/ats/candidate-status/${encodeURIComponent(identifier.trim())}`);
      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Candidate record not found for: ' + identifier);
      }

      setStatusData(data);
      if (data.primarySubmission?.SubmissionId) {
        setSelectedSubmissionId(data.primarySubmission.SubmissionId);
      } else if (data.submissions?.[0]?.SubmissionId) {
        setSelectedSubmissionId(data.submissions[0].SubmissionId);
      }
      if (data.candidate) {
        if (data.candidate.candidateCode) {
          localStorage.setItem('candidateCode', data.candidate.candidateCode);
          if (loggedInUser || userObj.email) {
            localStorage.setItem('candidateCodeUser', (loggedInUser || userObj.email).toLowerCase());
          }
        }
        if (data.candidate.candidateId) localStorage.setItem('candidateId', data.candidate.candidateId);
      }
    } catch (err) {
      console.error('❌ Error fetching status:', err);
      setError(err.message || 'Could not retrieve candidate status');
      setStatusData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleClearSearch = () => {
    localStorage.removeItem('candidateCode');
    localStorage.removeItem('candidateId');
    localStorage.removeItem('candidateCodeUser');
    setSearchQuery('');
    setError('');
    setStatusData(null);
    setLoading(false);
    navigate('/track-status', { replace: true });
  };

  useEffect(() => {
    const initFetch = async () => {
      let targetQuery = initialQuery;

      const token = localStorage.getItem('token');
      if (isLoggedIn && isCandidateUser && token && (!targetQuery || targetQuery.trim() === '')) {
        try {
          const meRes = await fetch(`${BASE_URL}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (meRes.ok) {
            const meData = await meRes.json();
            if (meData) {
              if (meData.candidateCode) localStorage.setItem('candidateCode', meData.candidateCode);
              if (meData.candidateId) localStorage.setItem('candidateId', meData.candidateId.toString());
              if (meData.username) localStorage.setItem('username', meData.username);
              targetQuery = meData.candidateCode || 
                (meData.candidateId ? meData.candidateId.toString() : '') || 
                meData.email || 
                meData.username;
            }
          }
        } catch (authErr) {
          console.warn('Could not query /api/auth/me:', authErr.message);
        }
      }

      if (targetQuery && targetQuery.trim()) {
        setSearchQuery(targetQuery.trim());
        await fetchStatus(targetQuery.trim());
      } else {
        setLoading(false);
      }
    };

    initFetch();
  }, [initialQuery, isLoggedIn, isCandidateUser]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      fetchStatus(searchQuery.trim());
      navigate(`/track-status?q=${encodeURIComponent(searchQuery.trim())}`, { replace: true });
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate('/login');
  };

  const getStageIcon = (index) => {
    switch (index) {
      case 1: return <FileCheck size={18} />;
      case 2: return <UserCheck size={18} />;
      case 3: return <Send size={18} />;
      case 4: return <Briefcase size={18} />;
      case 5: return <Sparkles size={18} />;
      default: return <Clock size={18} />;
    }
  };

  const candidate = statusData?.candidate || {};
  const initials = [candidate.firstName?.[0], candidate.lastName?.[0]].filter(Boolean).join('').toUpperCase() || 'C';

  const activeSubmission = (statusData?.submissions || []).find(s => String(s.SubmissionId) === String(selectedSubmissionId)) 
    || statusData?.primarySubmission 
    || statusData?.submissions?.[0] 
    || null;

  const activeMilestones = activeSubmission?.milestoneData || {
    currentStageIndex: statusData?.currentStageIndex || 1,
    currentStageName: statusData?.currentStageName || 'Application Submitted',
    isRejected: statusData?.isRejected || false,
    isWithdrawn: statusData?.isWithdrawn || false,
    statusLabel: statusData?.statusLabel || 'ACTIVE',
    stages: statusData?.stages || []
  };

  const milestoneSteps = [
    { index: 1, name: 'Application Submitted' },
    { index: 2, name: 'Review & Screening' },
    { index: 3, name: 'Presented to Client' },
    { index: 4, name: 'Offer Extended' },
    { index: 5, name: 'Onboarded' }
  ];

  // Prepare candidate data for AddModal
  const preparedCandidateForModal = candidate?.candidateId ? {
    CandidateId: candidate.candidateId,
    FirstName: candidate.firstName || '',
    LastName: candidate.lastName || '',
    MiddleName: candidate.middleName || '',
    JobTitle: candidate.jobTitle || '',
    Email: candidate.email || '',
    Phone: candidate.phone || '',
    Mobile: candidate.phone || '',
    Gender: candidate.gender || '',
    MaritalStatus: candidate.maritalStatus || '',
    WorkAuthorization: candidate.workAuthorization || '',
    EmploymentType: candidate.employmentType || '',
    SecurityClearance: candidate.securityClearance || '',
    WillingToRelocate: candidate.willingToRelocate ?? false,
    IsBench: true,
    RemoteStatus: candidate.remoteStatus || 'Remote',
    YearsOfExperience: candidate.yearsExp ?? '',
    CurrentLocation: candidate.currentLocation || '',
    LocationCity: candidate.cityName || '',
    LocationState: candidate.stateCode || '',
    LocationCountry: candidate.countryName || 'US',
    ExpectedRateFrom: candidate.expectedRateFrom || '',
    ExpectedRateTo: candidate.expectedRateTo || '',
    ExpectedRateType: candidate.expectedRateType || 'Hourly',
    CurrentRate: candidate.currentRate || '',
    CurrentRateType: candidate.currentRateType || 'Hourly',
    ExpectedSalary: candidate.expectedSalary || '',
    CurrentSalary: candidate.currentSalary || '',
    NoticePeriod: candidate.noticePeriod || '',
    LinkedInUrl: candidate.linkedinUrl || '',
    GitHubUrl: candidate.githubUrl || '',
    TwitterUrl: candidate.twitterUrl || '',
    VideoResumeUrl: candidate.videoResumeUrl || '',
    ProfileSummary: candidate.profileSummary || '',
    Skills: (candidate.skills || []).map(s => ({
      SkillName: typeof s === 'string' ? s : (s.SkillName || s.skillName || s.name || '')
    })),
    WorkExperience: (candidate.workExperience || []).map(w => ({
      Company: w.Company || w.company || '',
      JobTitle: w.JobTitle || w.jobTitle || '',
      StartDate: w.StartDate || w.startDate || '',
      EndDate: w.EndDate || w.endDate || '',
      IsCurrent: w.IsCurrent ?? w.isCurrent ?? false,
      Description: w.Description || w.description || '',
      WorkExperienceId: w.WorkExperienceId || w.workExperienceId || null
    })),
    Education: (candidate.education || []).map(e => ({
      Institution: e.Institution || e.institution || '',
      Degree: e.Degree || e.degree || '',
      FieldOfStudy: e.FieldOfStudy || e.fieldOfStudy || '',
      StartDate: e.StartDate || e.startDate || '',
      EndDate: e.EndDate || e.endDate || '',
      GPA: e.GPA || e.gpa || '',
      EducationId: e.EducationId || e.educationId || null
    })),
    Certifications: (candidate.certifications || []).map(c => ({
      CertificationName: c.CertificationName || c.name || '',
      IssuingOrganization: c.IssuingOrganization || c.issuingOrg || '',
      IssueDate: c.IssueDate || c.issueDate || '',
      ExpiryDate: c.ExpiryDate || c.expiryDate || '',
      CredentialId: c.CredentialId || c.credentialId || '',
      CredentialUrl: c.CredentialUrl || c.credentialUrl || '',
      CertificationId: c.CertificationId || c.certificationId || null
    })),
    Documents: (candidate.documents || []).filter(d => !d.IsPrimaryResume).map(d => ({
      DocumentId: d.DocumentId,
      DocumentName: d.FileNameOriginal,
      DocumentType: d.DocumentType,
      FileSizeBytes: d.FileSizeBytes,
      UploadedOn: d.UploadedOn
    }))
  } : null;

  // Handle Full Candidate Update (including resume & documents)
  const handleUpdateCandidate = async (payload) => {
    try {
      setSaving(true);
      const candidateId = candidate.candidateId;
      if (!candidateId) {
        throw new Error('Candidate ID not found');
      }

      const formData = new FormData();

      // Basic Profile
      formData.append('FirstName', payload.firstName || '');
      formData.append('LastName', payload.lastName || '');
      formData.append('MiddleName', payload.middleName || '');
      formData.append('EmailID', payload.email || '');
      formData.append('Mobile', payload.mobile || payload.phone || '');
      formData.append('Phone', payload.phone || '');
      formData.append('JobTitle', payload.jobTitle || '');
      formData.append('YearsOfExperience', payload.yearsOfExperience || '');
      formData.append('Gender', payload.gender || '');
      formData.append('MaritalStatus', payload.maritalStatus || '');
      formData.append('Industry', payload.industry || '');
      formData.append('ProfileSummary', payload.profileSummary || '');
      formData.append('LinkedInProfile', payload.linkedInUrl || '');
      formData.append('GitHubUrl', payload.gitHubUrl || '');
      formData.append('TwitterUrl', payload.twitterUrl || '');
      formData.append('VideoResumeUrl', payload.videoResumeUrl || '');
      formData.append('CandidateStatus', payload.status || candidate.status || 'Available');
      formData.append('RemoteStatus', payload.remoteStatus || 'Remote');
      formData.append('WorkAuthorization', payload.workAuthorization || '');
      formData.append('EmploymentType', payload.employmentType || '');
      formData.append('SecurityClearance', payload.securityClearance || '');
      formData.append('WillingToRelocate', payload.willingToRelocate ? 'true' : 'false');
      formData.append('IsBench', 'true');

      // Location
      const locParts = [payload.locationCity, payload.locationState, payload.locationCountry].filter(Boolean);
      formData.append('CurrentLocation', locParts.join(', ') || payload.currentLocation || '');
      formData.append('LocationCity', payload.locationCity || '');
      formData.append('LocationState', payload.locationState || '');
      formData.append('LocationCountry', payload.locationCountry || '');

      // Compensation
      formData.append('ExpectedSalary', payload.expectedSalary || '');
      formData.append('CurrentSalary', payload.currentSalary || '');
      formData.append('ExpectedRateFrom', payload.expectedRateFrom || '');
      formData.append('ExpectedRateTo', payload.expectedRateTo || '');
      formData.append('ExpectedRateType', payload.expectedRateType || 'Hourly');
      formData.append('CurrentRate', payload.currentRate || '');
      formData.append('CurrentRateType', payload.currentRateType || 'Hourly');
      formData.append('NoticePeriod', payload.noticePeriod || '');

      // Skills
      const skillsArr = Array.isArray(payload.skills)
        ? payload.skills.map(s => typeof s === 'string' ? s : (s.skillName || s.SkillName || s.name)).filter(Boolean)
        : [];
      formData.append('Skills', JSON.stringify(skillsArr));

      // Work Experience
      const workArr = (payload.workExperience || [])
        .filter(w => w.company || w.Company || w.jobTitle || w.JobTitle)
        .map(w => ({
          Company: w.company || w.Company,
          JobTitle: w.jobTitle || w.JobTitle,
          StartDate: w.startDate || w.StartDate,
          EndDate: w.endDate || w.EndDate,
          IsCurrent: w.isCurrent ?? w.IsCurrent ?? false,
          Description: w.description || w.Description,
          WorkExperienceId: w.workExperienceId || w.WorkExperienceId || null
        }));
      formData.append('WorkExperience', JSON.stringify(workArr));

      // Education
      const eduArr = (payload.education || [])
        .filter(e => e.institution || e.Institution || e.degree || e.Degree)
        .map(e => ({
          Institution: e.institution || e.Institution,
          Degree: e.degree || e.Degree,
          FieldOfStudy: e.fieldOfStudy || e.FieldOfStudy,
          StartDate: e.startDate || e.StartDate,
          EndDate: e.endDate || e.EndDate,
          GPA: e.gpa || e.GPA,
          EducationId: e.educationId || e.EducationId || null
        }));
      formData.append('Education', JSON.stringify(eduArr));

      // Certifications
      const certArr = (payload.certifications || [])
        .filter(c => c.name || c.CertificationName)
        .map(c => ({
          CertificationName: c.name || c.CertificationName,
          IssuingOrganization: c.issuingOrg || c.IssuingOrganization,
          IssueDate: c.issueDate || c.IssueDate,
          ExpiryDate: c.expiryDate || c.ExpiryDate,
          CredentialId: c.credentialId || c.CredentialId,
          CredentialUrl: c.credentialUrl || c.CredentialUrl,
          CertificationId: c.certificationId || c.CertificationId || null
        }));
      formData.append('Certifications', JSON.stringify(certArr));

      // Primary Resume file if uploaded/changed
      if (payload.ResumeFile) {
        formData.append('ResumeUpload', payload.ResumeFile);
        formData.append('resume', payload.ResumeFile);
        formData.append('OverwriteResume', 'true');
      }

      // Additional Documents
      const newDocs = (payload.documents || []).filter(d => d.file instanceof File || d.File instanceof File);
      newDocs.forEach((doc, idx) => {
        const fileObj = doc.file || doc.File;
        formData.append(`AdditionalDoc_${idx}`, fileObj);
        formData.append(`AdditionalDoc_${idx}_Name`, doc.documentName || doc.DocumentName || fileObj.name);
        formData.append(`AdditionalDoc_${idx}_Type`, doc.documentType || doc.DocumentType || 'OTHER');
      });
      formData.append('AdditionalDocsCount', String(newDocs.length));

      const token = localStorage.getItem('token') || '';
      const res = await fetch(`${BASE_URL}/api/resumes/${candidateId}`, {
        method: 'PUT',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: formData
      });

      const resData = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(resData.error || resData.message || `Failed to update with status ${res.status}`);
      }

      Swal.fire({
        icon: 'success',
        title: 'Profile Updated',
        text: 'Candidate details, experience, education, resume, and documents updated successfully!',
        confirmButtonColor: '#3b5bfd'
      });

      setIsEditModalOpen(false);
      // Refresh profile data
      await fetchStatus(searchQuery || candidate.email || candidate.candidateCode || String(candidateId));

    } catch (err) {
      console.error('❌ Update candidate error:', err);
      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: err.message || 'Could not update profile. Please try again.',
        confirmButtonColor: '#3b5bfd'
      });
    } finally {
      setSaving(false);
    }
  };

  // Format Helper
  const fmtDate = (d) => {
    if (!d) return '—';
    try {
      const date = new Date(d);
      return isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
    } catch {
      return '—';
    }
  };

  const fmtRelativeTime = (d) => {
    if (!d) return '—';
    try {
      const date = new Date(d);
      if (isNaN(date.getTime())) return '—';
      const now = new Date();
      const diffMs = now - date;
      const diffSecs = Math.floor(diffMs / 1000);
      const diffMins = Math.floor(diffSecs / 60);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffHours < 1) return `${Math.max(1, diffMins)}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 30) return `${diffDays}d ago`;
      return date.toLocaleDateString();
    } catch {
      return '—';
    }
  };

  const fmtBytes = (bytes) => {
    if (!bytes || isNaN(bytes)) return '';
    const kb = bytes / 1024;
    if (kb < 1024) return `${Math.round(kb)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  const handleDownloadDoc = (doc) => {
    if (doc.StorageLocator && doc.StorageLocator.startsWith('http')) {
      window.open(doc.StorageLocator, '_blank');
    } else {
      const token = localStorage.getItem('token') || '';
      const docId = doc.DocumentId || candidate.candidateId;
      window.open(`${BASE_URL}/api/resumes/${docId}/download?token=${token}`, '_blank');
    }
  };

  const handlePreviewDoc = (doc) => {
    if (doc.StorageLocator && doc.StorageLocator.startsWith('http')) {
      window.open(doc.StorageLocator, '_blank');
    } else {
      const token = localStorage.getItem('token') || '';
      const docId = doc.DocumentId || candidate.candidateId;
      window.open(`${BASE_URL}/api/resumes/${docId}/preview?token=${token}`, '_blank');
    }
  };

  return (
    <div className={`cst-page-container ${isInternal ? 'cst-internal-mode' : ''}`}>
      {/* Top Navbar - Matches Deep Midnight Hero */}
      {!isInternal && (
        <div className="cst-top-navbar">
          <div className="cst-nav-inner">
            <div className="cst-nav-brand" onClick={handleClearSearch} style={{ cursor: 'pointer' }} title="Prophecy Technologies">
              <img src={prophecyLogo2} alt="Prophecy Logo Icon" className="cst-nav-logo-icon" />
              <img src={prophecyLogo} alt="Prophecy Technologies" className="cst-nav-logo-text" />
            </div>

            <div className="cst-nav-right">
              {isLoggedIn ? (
                <div className="cst-user-menu">
                  <div className="cst-user-pill">
                    <User size={15} />
                    <span className="cst-user-email">{loggedInUser || candidate.email}</span>
                  </div>
                  <button onClick={handleLogout} className="cst-logout-btn" title="Log Out">
                    <LogOut size={16} />
                    <span>Log Out</span>
                  </button>
                </div>
              ) : (
                <button onClick={() => navigate('/login')} className="cst-nav-login-btn">
                  <ArrowRight size={14} />
                  <span>Candidate Login</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Container when Candidate Data is Active or Loading */}
      {(loading || statusData) && (
        <div className="cst-main-content-wrap">
          {loading && (
          <div className="cst-loading-card">
            <RefreshCw className="spinning" size={40} color="#3b5bfd" />
            <h3 style={{ marginTop: '16px', marginBottom: '6px', fontSize: '18px', fontWeight: 600, color: '#1e293b' }}>
              Loading Candidate Profile
            </h3>
            <p style={{ margin: 0, fontSize: '14px', color: '#64748b' }}>
              Retrieving profile information, bench details, and placement tracking...
            </p>
          </div>
        )}

        {error && !loading && statusData && (
          <div className="cst-alert cst-alert-error">
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        )}

        {!loading && statusData && (
          <div className="cst-profile-wrapper">
            {/* Candidate Header Banner Card */}
            <div className="cst-hero-header-card">
              <div className="cst-hero-left">
                <div className="cst-avatar-circle">
                  {initials}
                </div>
                <div className="cst-hero-details">
                  <div className="cst-hero-title-row">
                    <h1 className="cst-candidate-main-name">
                      {candidate.candidateName || `${candidate.firstName} ${candidate.lastName}`}
                    </h1>
                    {candidate.isBench && (
                      <span className="cst-bench-tag">Bench</span>
                    )}
                  </div>
                  <p className="cst-candidate-main-role">
                    {candidate.jobTitle || 'Bench Consultant'}
                  </p>
                </div>
              </div>

              <div className="cst-hero-right">
                <button
                  className="cst-btn-edit-profile"
                  onClick={() => setIsEditModalOpen(true)}
                  title="Edit candidate profile, experience, education, resume and documents"
                >
                  <Edit size={15} />
                  <span>Edit Details</span>
                </button>
                <span className="cst-status-badge-chip">
                  Status: {candidate.status || 'Available'}
                </span>
              </div>
            </div>



            {/* TAB NAVIGATION BAR */}
            <div className="cst-tabs-navbar">
              <button
                className={`cst-tab-item ${activeTab === 'overview' ? 'active' : ''}`}
                onClick={() => setActiveTab('overview')}
              >
                Overview
              </button>
              <button
                className={`cst-tab-item ${activeTab === 'experience' ? 'active' : ''}`}
                onClick={() => setActiveTab('experience')}
              >
                Experience
              </button>
              <button
                className={`cst-tab-item ${activeTab === 'education' ? 'active' : ''}`}
                onClick={() => setActiveTab('education')}
              >
                Education
              </button>
              <button
                className={`cst-tab-item ${activeTab === 'certifications' ? 'active' : ''}`}
                onClick={() => setActiveTab('certifications')}
              >
                Certifications
              </button>
              <button
                className={`cst-tab-item ${activeTab === 'resume' ? 'active' : ''}`}
                onClick={() => setActiveTab('resume')}
              >
                Resume
              </button>
              <button
                className={`cst-tab-item ${activeTab === 'documents' ? 'active' : ''}`}
                onClick={() => setActiveTab('documents')}
              >
                Documents
              </button>
              <button
                className={`cst-tab-item ${activeTab === 'submissions' ? 'active' : ''}`}
                onClick={() => setActiveTab('submissions')}
              >
                Bench Submissions
                {statusData?.submissions?.length > 0 && (
                  <span className="cst-tab-count">{statusData.submissions.length}</span>
                )}
              </button>
              <button
                className={`cst-tab-item ${activeTab === 'applications' ? 'active' : ''}`}
                onClick={() => setActiveTab('applications')}
              >
                Application
                {statusData?.applications?.length > 0 && (
                  <span className="cst-tab-count">{statusData.applications.length}</span>
                )}
              </button>
            </div>

            {/* TAB CONTENT PANELS */}
            <div className="cst-tab-content-container">
              {/* 1. OVERVIEW TAB */}
              {activeTab === 'overview' && (
                <div className="cst-overview-grid">
                  {/* Personal Information */}
                  <div className="cst-info-card">
                    <div className="cst-card-header">
                      <User size={18} className="cst-card-icon" />
                      <h3>Personal Information</h3>
                    </div>
                    <div className="cst-card-rows">
                      <div className="cst-card-row">
                        <span className="cst-label">Full Name</span>
                        <span className="cst-val">{candidate.candidateName || `${candidate.firstName} ${candidate.lastName}`}</span>
                      </div>
                      <div className="cst-card-row">
                        <span className="cst-label">Gender</span>
                        <span className="cst-val">{candidate.gender || '—'}</span>
                      </div>
                      <div className="cst-card-row">
                        <span className="cst-label">Marital Status</span>
                        <span className="cst-val">{candidate.maritalStatus || '—'}</span>
                      </div>
                      <div className="cst-card-row">
                        <span className="cst-label">Candidate Code</span>
                        <span className="cst-val">{candidate.candidateCode || '—'}</span>
                      </div>
                      <div className="cst-card-row">
                        <span className="cst-label">Years of Experience</span>
                        <span className="cst-val">{candidate.yearsExp ? `${candidate.yearsExp} yrs` : '—'}</span>
                      </div>
                      <div className="cst-card-row">
                        <span className="cst-label">Current Location</span>
                        <span className="cst-val">{candidate.currentLocation || '—'}</span>
                      </div>
                      <div className="cst-card-row">
                        <span className="cst-label">Willing to Relocate</span>
                        <span className="cst-val cst-val-yes">
                          {candidate.willingToRelocate ? <><CheckCircle2 size={14} /> Yes</> : 'No'}
                        </span>
                      </div>
                      <div className="cst-card-row">
                        <span className="cst-label">Bench Candidate</span>
                        <span className="cst-val cst-val-bench">
                          {candidate.isBench ? <><CheckCircle2 size={14} /> Yes — On Bench</> : 'No'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Contact & Social */}
                  <div className="cst-info-card">
                    <div className="cst-card-header">
                      <Mail size={18} className="cst-card-icon" />
                      <h3>Contact & Social</h3>
                    </div>
                    <div className="cst-card-rows">
                      <div className="cst-card-row">
                        <span className="cst-label">Email</span>
                        <span className="cst-val">{candidate.email || '—'}</span>
                      </div>
                      <div className="cst-card-row">
                        <span className="cst-label">Mobile</span>
                        <span className="cst-val">{candidate.phone || '—'}</span>
                      </div>
                      <div className="cst-card-row">
                        <span className="cst-label">LinkedIn</span>
                        <span className="cst-val">
                          {candidate.linkedinUrl ? (
                            <a href={candidate.linkedinUrl} target="_blank" rel="noopener noreferrer" className="cst-link">
                              {candidate.linkedinUrl} <ExternalLink size={12} />
                            </a>
                          ) : '—'}
                        </span>
                      </div>
                      <div className="cst-card-row">
                        <span className="cst-label">GitHub</span>
                        <span className="cst-val">
                          {candidate.githubUrl ? (
                            <a href={candidate.githubUrl} target="_blank" rel="noopener noreferrer" className="cst-link">
                              {candidate.githubUrl} <ExternalLink size={12} />
                            </a>
                          ) : '—'}
                        </span>
                      </div>
                      <div className="cst-card-row">
                        <span className="cst-label">Twitter</span>
                        <span className="cst-val">{candidate.twitterUrl || '—'}</span>
                      </div>
                      <div className="cst-card-row">
                        <span className="cst-label">Video Resume</span>
                        <span className="cst-val">
                          {candidate.videoResumeUrl ? (
                            <a href={candidate.videoResumeUrl} target="_blank" rel="noopener noreferrer" className="cst-link">
                              View Video <ExternalLink size={12} />
                            </a>
                          ) : '—'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Employment Details */}
                  <div className="cst-info-card">
                    <div className="cst-card-header">
                      <Briefcase size={18} className="cst-card-icon" />
                      <h3>Employment Details</h3>
                    </div>
                    <div className="cst-card-rows">
                      <div className="cst-card-row">
                        <span className="cst-label">Work Authorization</span>
                        <span className="cst-val">{candidate.workAuthorization || '—'}</span>
                      </div>
                      <div className="cst-card-row">
                        <span className="cst-label">Employment Type</span>
                        <span className="cst-val">{candidate.employmentType || '—'}</span>
                      </div>
                      <div className="cst-card-row">
                        <span className="cst-label">Security Clearance</span>
                        <span className="cst-val">{candidate.securityClearance || '—'}</span>
                      </div>
                      <div className="cst-card-row">
                        <span className="cst-label">Industry</span>
                        <span className="cst-val">{candidate.industry || '—'}</span>
                      </div>
                      <div className="cst-card-row">
                        <span className="cst-label">Current Employer</span>
                        <span className="cst-val">{candidate.currentEmployer || '—'}</span>
                      </div>
                      <div className="cst-card-row">
                        <span className="cst-label">Current Salary</span>
                        <span className="cst-val">{candidate.currentSalary || '—'}</span>
                      </div>
                      <div className="cst-card-row">
                        <span className="cst-label">Expected Salary</span>
                        <span className="cst-val">{candidate.expectedSalary || '—'}</span>
                      </div>
                      <div className="cst-card-row">
                        <span className="cst-label">Notice Period</span>
                        <span className="cst-val">{candidate.noticePeriod || '—'}</span>
                      </div>
                      <div className="cst-card-row">
                        <span className="cst-label">Remote Status</span>
                        <span className="cst-val">
                          {candidate.remoteStatus ? (
                            <span className="cst-pill-remote">{candidate.remoteStatus}</span>
                          ) : '—'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Compensation */}
                  <div className="cst-info-card">
                    <div className="cst-card-header">
                      <DollarSign size={18} className="cst-card-icon" />
                      <h3>Compensation</h3>
                    </div>
                    <div className="cst-card-rows">
                      {candidate.expectedRateFrom || candidate.expectedRateTo ? (
                        <>
                          <div className="cst-card-row">
                            <span className="cst-label">Expected Rate</span>
                            <span className="cst-val">${candidate.expectedRateFrom} - ${candidate.expectedRateTo} / {candidate.expectedRateType || 'hr'}</span>
                          </div>
                          <div className="cst-card-row">
                            <span className="cst-label">Current Rate</span>
                            <span className="cst-val">{candidate.currentRate ? `$${candidate.currentRate} / ${candidate.currentRateType || 'hr'}` : '—'}</span>
                          </div>
                        </>
                      ) : (
                        <p className="cst-muted-text">No compensation information available</p>
                      )}
                    </div>
                  </div>

                  {/* Application Card (Commented out)
                  <div className="cst-info-card">
                    <div className="cst-card-header">
                      <FileCheck size={18} className="cst-card-icon" />
                      <h3>Application</h3>
                    </div>
                    <div className="cst-card-rows">
                      <div className="cst-card-row">
                        <span className="cst-label">Status</span>
                        <span className="cst-val"><span className="cst-pill-available">{candidate.status || 'Available'}</span></span>
                      </div>
                      <div className="cst-card-row">
                        <span className="cst-label">Job Title</span>
                        <span className="cst-val">{candidate.jobTitle || '—'}</span>
                      </div>
                      <div className="cst-card-row">
                        <span className="cst-label">Created Date</span>
                        <span className="cst-val">{fmtDate(candidate.appliedAt)}</span>
                      </div>
                      <div className="cst-card-row">
                        <span className="cst-label">Last Updated</span>
                        <span className="cst-val">{fmtDate(candidate.updatedAt || candidate.appliedAt)}</span>
                      </div>
                    </div>
                  </div>
                  */}

                  {/* Bench Details */}
                  <div className="cst-info-card">
                    <div className="cst-card-header">
                      <UserCheck2 size={18} className="cst-card-icon" />
                      <h3>Bench Details</h3>
                    </div>
                    <div className="cst-card-rows">
                      <div className="cst-card-row">
                        <span className="cst-label">Bench Status</span>
                        <span className="cst-val">{candidate.isBench ? 'Active on Bench' : 'Standard Applicant'}</span>
                      </div>
                      <div className="cst-card-row">
                        <span className="cst-label">Days on Bench</span>
                        <span className="cst-val">
                          <span className="cst-pill-days">0 days (Fresh)</span>
                        </span>
                      </div>
                      <div className="cst-card-row">
                        <span className="cst-label">Work Auth Petitioner</span>
                        <span className="cst-val">—</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 2. EXPERIENCE TAB */}
              {activeTab === 'experience' && (
                <div className="cst-section-card">
                  <div className="cst-section-card-header">
                    <Briefcase size={20} className="cst-card-icon" />
                    <h3>Work Experience ({candidate.workExperience?.length || 0})</h3>
                  </div>
                  {candidate.workExperience && candidate.workExperience.length > 0 ? (
                    <div className="cst-timeline-list">
                      {candidate.workExperience.map((exp, idx) => (
                        <div key={exp.WorkExperienceId || idx} className="cst-timeline-item">
                          <div className="cst-timeline-bullet"></div>
                          <div className="cst-timeline-body">
                            <div className="cst-timeline-head">
                              <h4 className="cst-timeline-role">{exp.JobTitle || 'Professional Role'}</h4>
                              <span className="cst-timeline-company">{exp.Company || 'Company'}</span>
                            </div>
                            <span className="cst-timeline-date">
                              <Calendar size={13} />
                              {fmtDate(exp.StartDate)} — {exp.IsCurrent ? 'Present' : fmtDate(exp.EndDate)}
                            </span>
                            {exp.Description && (
                              <p className="cst-timeline-desc">{exp.Description}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="cst-tab-empty">
                      <Briefcase size={36} />
                      <p>No work experience records listed</p>
                    </div>
                  )}
                </div>
              )}

              {/* 3. EDUCATION TAB */}
              {activeTab === 'education' && (
                <div className="cst-section-card">
                  <div className="cst-section-card-header">
                    <GraduationCap size={20} className="cst-card-icon" />
                    <h3>Education ({candidate.education?.length || 0})</h3>
                  </div>
                  {candidate.education && candidate.education.length > 0 ? (
                    <div className="cst-edu-grid">
                      {candidate.education.map((edu, idx) => (
                        <div key={edu.EducationId || idx} className="cst-edu-card">
                          <div className="cst-edu-icon">
                            <GraduationCap size={22} />
                          </div>
                          <div className="cst-edu-body">
                            <h4 className="cst-edu-degree">{edu.Degree || 'Degree / Diploma'}</h4>
                            <p className="cst-edu-inst">{edu.Institution || 'Institution'}</p>
                            {edu.FieldOfStudy && <p className="cst-edu-field">Field: {edu.FieldOfStudy}</p>}
                            <div className="cst-edu-meta">
                              {(edu.StartDate || edu.EndDate) && (
                                <span>{fmtDate(edu.StartDate)} - {fmtDate(edu.EndDate)}</span>
                              )}
                              {edu.GPA && <span className="cst-gpa-badge">GPA: {edu.GPA}</span>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="cst-tab-empty">
                      <GraduationCap size={36} />
                      <p>No education records listed</p>
                    </div>
                  )}
                </div>
              )}

              {/* 3. CERTIFICATIONS TAB */}
              {activeTab === 'certifications' && (
                <div className="cst-section-card">
                  <div className="cst-section-card-header">
                    <Award size={20} className="cst-card-icon" />
                    <h3>Certifications ({candidate.certifications?.length || 0})</h3>
                  </div>
                  {candidate.certifications && candidate.certifications.length > 0 ? (
                    <div className="cst-edu-grid">
                      {candidate.certifications.map((cert, idx) => (
                        <div key={cert.CertificationId || idx} className="cst-edu-card">
                          <div className="cst-cert-icon">
                            <Award size={22} />
                          </div>
                          <div className="cst-edu-body">
                            <h4 className="cst-edu-degree">{cert.CertificationName || cert.name || 'Certification'}</h4>
                            <p className="cst-edu-inst">{cert.IssuingOrganization || cert.issuingOrg || 'Issuing Organization'}</p>
                            {cert.CredentialId && <p className="cst-edu-field">Credential ID: {cert.CredentialId}</p>}
                            <div className="cst-edu-meta">
                              {(cert.IssueDate || cert.ExpiryDate) && (
                                <span>
                                  {cert.IssueDate ? `Issued: ${fmtDate(cert.IssueDate)}` : ''}
                                  {cert.ExpiryDate ? ` • Expires: ${fmtDate(cert.ExpiryDate)}` : ''}
                                </span>
                              )}
                              {cert.CredentialUrl && (
                                <a
                                  href={cert.CredentialUrl.startsWith('http') ? cert.CredentialUrl : `https://${cert.CredentialUrl}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="cst-link"
                                >
                                  View Credential <ExternalLink size={11} />
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="cst-tab-empty">
                      <Award size={36} />
                      <p>No certifications listed</p>
                    </div>
                  )}
                </div>
              )}

              {/* 4. RESUME TAB */}
              {activeTab === 'resume' && (() => {
                const primaryResume = (candidate.documents || []).find(d => d.IsPrimaryResume || (d.DocumentType || '').toUpperCase() === 'RESUME');
                return (
                  <div className="cst-section-card">
                    <div className="cst-section-card-header">
                      <FileText size={20} className="cst-card-icon" />
                      <h3>Resume Document</h3>
                    </div>
                    {primaryResume ? (
                      <div className="cst-resume-viewer-card">
                        <div className="cst-resume-info-row">
                          <div className="cst-resume-file-icon">
                            <FileText size={32} />
                          </div>
                          <div className="cst-resume-meta">
                            <h4 className="cst-resume-filename">{primaryResume.FileNameOriginal || 'Resume'}</h4>
                            <div className="cst-resume-tags">
                              {primaryResume.FileSizeBytes && (
                                <span className="cst-doc-chip">{fmtBytes(primaryResume.FileSizeBytes)}</span>
                              )}
                              <span className="cst-doc-chip">Uploaded: {fmtDate(primaryResume.UploadedOn)}</span>
                              {primaryResume.ExtractedTitle && (
                                <span className="cst-doc-chip cst-chip-teal">{primaryResume.ExtractedTitle}</span>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => handleDownloadDoc(primaryResume)}
                            className="cst-btn-download-resume"
                            title="Download Resume"
                          >
                            <Download size={15} />
                            <span>Download</span>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="cst-tab-empty">
                        <FileText size={36} />
                        <p>No resume document available</p>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* 5. DOCUMENTS TAB */}
              {activeTab === 'documents' && (
                <div className="cst-section-card">
                  <div className="cst-section-card-header">
                    <FileCheck size={20} className="cst-card-icon" />
                    <h3>Additional Documents ({candidate.documents?.filter(d => !d.IsPrimaryResume)?.length || 0})</h3>
                  </div>
                  {candidate.documents && candidate.documents.filter(d => !d.IsPrimaryResume).length > 0 ? (
                    <div className="cst-docs-table-wrap">
                      <table className="cst-docs-table">
                        <thead>
                          <tr>
                            <th>Document Name</th>
                            <th>Type</th>
                            <th>File Size</th>
                            <th>Uploaded On</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {candidate.documents.filter(d => !d.IsPrimaryResume).map((doc, idx) => (
                            <tr key={doc.DocumentId || idx}>
                              <td>
                                <div className="cst-doc-name-cell">
                                  <FileText size={16} />
                                  <span>{doc.FileNameOriginal || 'Document'}</span>
                                </div>
                              </td>
                              <td>
                                <span className="cst-doctype-pill">{doc.DocumentType || 'OTHER'}</span>
                              </td>
                              <td>{fmtBytes(doc.FileSizeBytes) || '—'}</td>
                              <td>{fmtDate(doc.UploadedOn)}</td>
                              <td>
                                <button
                                  onClick={() => handleDownloadDoc(doc)}
                                  className="cst-table-download-btn"
                                >
                                  <Download size={14} />
                                  <span>Download</span>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="cst-tab-empty">
                      <FileCheck size={36} />
                      <p>No additional documents available</p>
                    </div>
                  )}
                </div>
              )}
              {/* 6. SUBMISSIONS TAB */}
              {activeTab === 'submissions' && (
                <div className="cst-section-card">
                  <div className="cst-section-card-header">
                    <Building2 size={20} className="cst-card-icon" />
                    <h3>Bench Submissions ({statusData?.submissions?.length || 0})</h3>
                  </div>
                  {statusData?.submissions && statusData.submissions.length > 0 ? (
                    <>
                      {/* Desktop Table */}
                      <div className="cst-submissions-table-wrap">
                        <table className="cst-submissions-table">
                          <thead>
                            <tr>
                              <th>Client</th>
                              <th>Job Title</th>
                              <th>Status</th>
                              <th>Stage / Sub-Status</th>
                              <th>Technical Progress</th>
                              <th>Submitted Date</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {statusData.submissions.map((sub, sIdx) => {
                              const isSel = String(sub.SubmissionId) === String(activeSubmission?.SubmissionId);
                              const mData = sub.milestoneData || {};
                              const isSubRej = mData.isRejected || (sub.SubmissionStatusCode || '').toUpperCase() === 'REJECTED';
                              const isSubHired = mData.statusLabel === 'HIRED' || (sub.CurrentStageCode || '').toUpperCase() === 'HIRED';
                              
                              const badgeClass = isSubRej ? 'rejected'
                                : isSubHired ? 'hired'
                                  : (sub.SubmissionStatusCode || '').toUpperCase().includes('OFFER') ? 'offer'
                                    : 'active';

                              return (
                                <tr 
                                  key={sub.SubmissionId || sIdx} 
                                  className={`cst-sub-table-row ${isSel ? 'cst-row-active-selected' : ''}`}
                                  onClick={() => setSelectedSubmissionId(sub.SubmissionId)}
                                  style={{ cursor: 'pointer' }}
                                  title="Click to select this submission"
                                >
                                  <td>
                                    <div className="cst-sub-client-cell">
                                      <div className="cst-sub-client-icon">
                                        <Building2 size={14} />
                                      </div>
                                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <span style={{ fontWeight: 600, color: '#0f172a' }}>{sub.ClientName || 'Direct Client'}</span>
                                        {isSel && <span className="cst-viewing-badge">● Selected</span>}
                                      </div>
                                    </div>
                                  </td>
                                  <td>{sub.JobTitle || '—'}</td>
                                  <td>
                                    <span className={`cst-sub-status-badge ${badgeClass}`}>
                                      {mData.statusLabel || sub.SubmissionStatusCode || 'ACTIVE'}
                                    </span>
                                  </td>
                                  <td>
                                    <span style={{ fontSize: '13px', color: '#475569', fontWeight: 500 }}>
                                      {mData.currentStageName || sub.CurrentStageCode || 'Application Submitted'}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="cst-progress-chevron-bar">
                                      {milestoneSteps.map((step) => {
                                        const isCurrent = !isSubRej && step.index === (mData.currentStageIndex || 1);
                                        const isCompleted = !isSubRej && step.index < (mData.currentStageIndex || 1);
                                        const isStepRej = isSubRej && step.index === (mData.currentStageIndex || 1);

                                        let statusClass = 'pending';
                                        let countVal = 0;
                                        let tipMsg = `${step.name}: Pending`;

                                        if (isStepRej) {
                                          statusClass = 'rejected';
                                          countVal = 1;
                                          tipMsg = `${step.name}: Application Concluded`;
                                        } else if (isCurrent) {
                                          statusClass = 'active';
                                          countVal = 1;
                                          tipMsg = `${step.name}: 1 Active Candidate`;
                                        } else if (isCompleted) {
                                          statusClass = 'completed';
                                          countVal = 0;
                                          tipMsg = `${step.name}: Completed`;
                                        }

                                        return (
                                          <div
                                            key={step.index}
                                            className="cst-chevron-step-wrap"
                                            title={tipMsg}
                                          >
                                            <div className={`cst-chevron-step step-${step.index} ${statusClass} ${step.index === 1 ? 'is-first' : ''}`}>
                                              <span>{countVal}</span>
                                            </div>
                                            <div className="cst-chevron-tooltip">
                                              {tipMsg}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </td>
                                  <td>{fmtDate(sub.SubmittedAtUtc || sub.CreatedAtUtc)}</td>
                                  <td>
                                    <button 
                                      className={`cst-track-sub-btn ${isSel ? 'active' : ''}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedSubmissionId(sub.SubmissionId);
                                      }}
                                    >
                                      {isSel ? 'Active' : 'Select'}
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {/* Mobile Card List */}
                      <div className="cst-sub-mobile-card">
                        {statusData.submissions.map((sub, sIdx) => {
                          const isSel = String(sub.SubmissionId) === String(activeSubmission?.SubmissionId);
                          const mData = sub.milestoneData || {};
                          const isSubRej = mData.isRejected || (sub.SubmissionStatusCode || '').toUpperCase() === 'REJECTED';
                          const isSubHired = mData.statusLabel === 'HIRED' || (sub.CurrentStageCode || '').toUpperCase() === 'HIRED';
                          const badgeClass = isSubRej ? 'rejected' : isSubHired ? 'hired' : 'active';

                          return (
                            <div 
                              key={sub.SubmissionId || sIdx} 
                              className={`cst-sub-mobile-item ${isSel ? 'cst-mobile-selected' : ''}`}
                              onClick={() => setSelectedSubmissionId(sub.SubmissionId)}
                            >
                              <div className="cst-sub-mobile-row">
                                <span className="cst-sub-mobile-label">Client</span>
                                <span className="cst-sub-mobile-val" style={{ fontWeight: 600 }}>{sub.ClientName || 'Direct Client'}</span>
                              </div>
                              <div className="cst-sub-mobile-row">
                                <span className="cst-sub-mobile-label">Job Title</span>
                                <span className="cst-sub-mobile-val">{sub.JobTitle || '—'}</span>
                              </div>
                              <div className="cst-sub-mobile-row">
                                <span className="cst-sub-mobile-label">Status</span>
                                <span className={`cst-sub-status-badge ${badgeClass}`}>
                                  {mData.statusLabel || sub.SubmissionStatusCode || 'ACTIVE'}
                                </span>
                              </div>
                              <div className="cst-sub-mobile-row">
                                <span className="cst-sub-mobile-label">Stage</span>
                                <span className="cst-sub-mobile-val">{mData.currentStageName || sub.CurrentStageCode || '—'}</span>
                              </div>
                              <div className="cst-sub-mobile-row">
                                <span className="cst-sub-mobile-label">Submitted</span>
                                <span className="cst-sub-mobile-val">{fmtDate(sub.SubmittedAtUtc || sub.CreatedAtUtc)}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <div className="cst-tab-empty">
                      <Building2 size={36} />
                      <p>No bench submissions found</p>
                      <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>This candidate has not been submitted to any jobs yet.</p>
                    </div>
                  )}
                </div>
              )}
              {/* 7. APPLICATION TAB (Careers Portal Job Applications) */}
              {activeTab === 'applications' && (
                <div className="cst-section-card">
                  <div className="cst-section-card-header">
                    <FileText size={20} className="cst-card-icon" />
                    <h3>Careers Portal Applications ({statusData?.applications?.length || 0})</h3>
                  </div>

                  {statusData?.applications && statusData.applications.length > 0 ? (
                    <div className="cst-app-table-wrap">
                      <table className="cst-app-table">
                        <thead>
                          <tr>
                            <th>CLIENT</th>
                            <th>JOB ID</th>
                            <th>EXTERNAL JOB REF</th>
                            <th>JOB TITLE</th>
                            <th>TYPE</th>
                            <th>EXP</th>
                            <th>LOCATION</th>
                            <th>TECHNICAL PROGRESS</th>
                            <th>CREATED</th>
                          </tr>
                        </thead>
                        <tbody>
                          {statusData.applications.map((app, aIdx) => {
                            const mData = app.milestoneData || {};
                            const activeStepIdx = mData.currentStageIndex || 1;
                            const isRej = mData.isRejected;

                            return (
                              <tr key={app.ApplicationId || app.id || aIdx} className="cst-app-table-row">
                                <td className="cst-app-client-cell">
                                  <div className="cst-app-client-wrap">
                                    <div className="cst-app-client-icon">
                                      <Building2 size={15} />
                                    </div>
                                    <span className="cst-app-client-name">{app.ClientName || 'Careers Portal'}</span>
                                  </div>
                                </td>
                                <td className="cst-app-jobid-cell">
                                  {app.JobCode || (app.job_id ? app.job_id : `APP-${String(app.id || aIdx + 1).padStart(5, '0')}`)}
                                </td>
                                <td className="cst-app-extref-cell">
                                  <span className="cst-app-extref-tag">
                                    {app.ExternalJobReference || '—'}
                                  </span>
                                </td>
                                <td className="cst-app-title-cell">
                                  <span className="cst-app-jobtitle">{app.JobTitle || app.position || 'Open Position'}</span>
                                </td>
                                <td className="cst-app-type-cell">
                                  <span className="cst-app-type-badge">
                                    {app.EmploymentType || 'Full-time'}
                                  </span>
                                </td>
                                <td className="cst-app-exp-cell">
                                  {app.Experience ? `${app.Experience}${typeof app.Experience === 'number' ? ' yrs' : ''}` : (candidate.yearsExp ? `${candidate.yearsExp} yrs` : '—')}
                                </td>
                                <td className="cst-app-loc-cell" title={app.Location || candidate.currentLocation || 'Remote, US'}>
                                  <span className="cst-app-loc-text">
                                    {app.Location || candidate.currentLocation || 'Remote, US'}
                                  </span>
                                </td>
                                <td className="cst-app-progress-cell">
                                  <div className="cst-progress-chevron-bar">
                                    {milestoneSteps.map((step) => {
                                      const isCurrent = !isRej && step.index === activeStepIdx;
                                      const isCompleted = !isRej && step.index < activeStepIdx;
                                      const isStepRej = isRej && step.index === activeStepIdx;

                                      let statusClass = 'pending';
                                      let countVal = 0;
                                      let tipMsg = `${step.name}: Pending`;

                                      if (isStepRej) {
                                        statusClass = 'rejected';
                                        countVal = 1;
                                        tipMsg = `${step.name}: Application Concluded`;
                                      } else if (isCurrent) {
                                        statusClass = 'active';
                                        countVal = 1;
                                        tipMsg = `${step.name}: 1 Active Candidate`;
                                      } else if (isCompleted) {
                                        statusClass = 'completed';
                                        countVal = 0;
                                        tipMsg = `${step.name}: Completed`;
                                      }

                                      return (
                                        <div
                                          key={step.index}
                                          className="cst-chevron-step-wrap"
                                          title={tipMsg}
                                        >
                                          <div className={`cst-chevron-step step-${step.index} ${statusClass} ${step.index === 1 ? 'is-first' : ''}`}>
                                            <span>{countVal}</span>
                                          </div>
                                          <div className="cst-chevron-tooltip">
                                            {tipMsg}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </td>
                                <td className="cst-app-created-cell">
                                  {fmtRelativeTime(app.CreatedAtUtc)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="cst-tab-empty">
                      <FileText size={36} />
                      <p>No careers portal applications found</p>
                      <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
                        When you apply for open roles on our Careers Portal, your job applications and recruitment progress will be tracked here.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* 🚀 5-STEP LINEAR ATS PIPELINE (Commented out in favor of Application Tab Technical Progress)
            <div className={`cst-linear-pipeline-card ${activeMilestones.isRejected ? 'is-rejected' : ''}`}>
              <div className="cst-pipe-card-header">
                <div className="cst-pipe-header-left">
                  <span className="cst-pipe-status-label">Status:</span>
                  <span className={`cst-pipe-status-value ${activeMilestones.isRejected ? 'rejected' : activeMilestones.statusLabel === 'HIRED' ? 'hired' : 'active'}`}>
                    {activeMilestones.statusLabel || activeSubmission?.SubmissionStatusCode || candidate.status || 'ACTIVE'}
                  </span>
                  {activeSubmission?.ClientName && (
                    <span className="cst-pipe-client-tag">
                      {activeSubmission.ClientName} — {activeSubmission.JobTitle || 'Open Position'}
                    </span>
                  )}
                </div>
                {activeMilestones.currentStageName && (
                  <div className="cst-pipe-current-stage-tag">
                    <span>
                      Milestone {activeMilestones.currentStageIndex} of 5:{' '}
                      <strong>{activeMilestones.currentStageName}</strong>
                    </span>
                  </div>
                )}
              </div>

              {activeMilestones.isRejected && (
                <div className="cst-rejection-notice-box">
                  <div className="cst-rej-icon-wrap">
                    <AlertCircle size={22} className="cst-rej-icon" />
                  </div>
                  <div className="cst-rej-content">
                    <h4 className="cst-rej-title">Application Status Notice</h4>
                    <p className="cst-rej-msg">
                      Thank you for your interest and time invested with us for the{' '}
                      <strong>{activeSubmission?.JobTitle || 'role'}</strong> position at{' '}
                      <strong>{activeSubmission?.ClientName || 'our client'}</strong>. While we were impressed by your qualifications, the hiring team has concluded this specific process as requirements have evolved.
                    </p>
                    <p className="cst-rej-bench-note">
                      ✨ <strong>What's Next:</strong> Your profile remains actively maintained on our <strong>Bench Network</strong>. Our talent team continues to match your profile with upcoming client requisitions.
                    </p>
                  </div>
                </div>
              )}

              <div className="cst-pipeline-track-wrapper">
                <div className="cst-linear-pipeline">
                  <div className="cst-linear-track-bg">
                    <div
                      className={`cst-linear-track-fill ${activeMilestones.isRejected ? 'track-rejected' : ''}`}
                      style={{
                        width: `${Math.min(100, Math.max(0, ((activeMilestones.currentStageIndex - 1) / (activeMilestones.stages.length - 1)) * 100))}%`
                      }}
                    ></div>
                  </div>

                  <div className="cst-linear-nodes-row">
                    {activeMilestones.stages.map((stage) => {
                      const stepNum = stage.index;
                      const isRejected = stage.isRejected || (activeMilestones.isRejected && stepNum === activeMilestones.currentStageIndex);
                      const isCompleted = !isRejected && (stage.completed || (!activeMilestones.isRejected && stepNum < activeMilestones.currentStageIndex));
                      const isCurrent = !isRejected && stepNum === activeMilestones.currentStageIndex;

                      let nodeClass = '';
                      if (isRejected) nodeClass = 'rejected';
                      else if (isCompleted) nodeClass = 'completed';
                      else if (isCurrent) nodeClass = 'current';
                      else nodeClass = 'pending';

                      return (
                        <div
                          key={stage.key || stepNum}
                          className={`cst-lnode-col ${nodeClass}`}
                        >
                          <div className="cst-lnode-circle-wrap">
                            <div className="cst-lnode-circle">
                              {isRejected ? (
                                <X size={16} strokeWidth={2.6} />
                              ) : isCompleted ? (
                                <CheckCircle size={16} />
                              ) : (
                                stepNum
                              )}
                            </div>
                          </div>
                          <span className="cst-lnode-label">
                            {stage.displayLabel || stage.title?.toUpperCase()}
                          </span>
                          {stage.subStatus && (
                            <span className={`cst-lnode-substatus ${isRejected ? 'rejected' : isCurrent ? 'current' : ''}`}>
                              {stage.subStatus}
                            </span>
                          )}
                          {stage.date && (
                            <span className="cst-lnode-date">
                              {fmtDate(stage.date)}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {statusData.submissions && statusData.submissions.length > 0 && (
                  <div className="cst-client-subs-strip">
                    <h4 className="cst-subs-strip-title">Select Active Submission to Track Progress</h4>
                    <div className="cst-subs-cards-grid">
                      {statusData.submissions.map((sub, sIdx) => {
                        const isSelectedCard = String(sub.SubmissionId) === String(activeSubmission?.SubmissionId);
                        const mData = sub.milestoneData || {};
                        const isSubRej = mData.isRejected || (sub.SubmissionStatusCode || '').toUpperCase() === 'REJECTED';
                        const isSubHired = mData.statusLabel === 'HIRED';

                        return (
                          <div 
                            key={sub.SubmissionId || sIdx} 
                            className={`cst-sub-mini-card ${isSelectedCard ? 'active-selected' : ''}`}
                            onClick={() => setSelectedSubmissionId(sub.SubmissionId)}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="cst-sub-mini-icon">
                              <Building2 size={18} />
                            </div>
                            <div className="cst-sub-mini-body">
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span className="cst-sub-client-name">{sub.ClientName || 'Direct Client'}</span>
                                {isSelectedCard && <span className="cst-mini-selected-dot">●</span>}
                              </div>
                              <span className="cst-sub-role-name">{sub.JobTitle || 'Open Position'}</span>
                            </div>
                            <span className={`cst-sub-stage-badge ${isSubRej ? 'rejected' : isSubHired ? 'hired' : 'active'}`}>
                              {mData.statusLabel || sub.SubmissionStatusCode || 'ACTIVE'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
            */}
          </div>
        )}
        </div>
      )}

      {/* Landing View: Animated Cyber Hero & Workspace Search matching Prophecy Careers Portal */}
      {!statusData && !loading && (
        <>
          {/* SECTION 1: ANIMATED CYBER HERO */}
          <section className="cst-hero-wrapper">
            <div
              className="cst-hero-bg-layer"
              style={{ backgroundImage: `url(${careersHeroBg})` }}
            />
            <div className="cst-hero-container">
              <div className="cst-hero-grid">
                <div className="cst-hero-intro">
                  <div className="cst-hero-eyebrow">
                    <Globe size={14} className="cst-hero-eyebrow-icon" />
                    <span>Global Talent Platform</span>
                    <span className="cst-hero-eyebrow-slash">/</span>
                    <span className="cst-hero-eyebrow-accent">Engineering & AI Careers</span>
                  </div>
                  <h1 className="cst-hero-title">
                    <span className="cst-hero-title-white">Great talent.</span>{' '}
                    <span className="cst-word-gradient-cyan-teal">Extraordinary possibilities.</span>
                  </h1>
                  <p className="cst-hero-lede">
                    Find your place at Prophecy. Explore opportunities to build, solve, and grow with teams shaping what comes next.
                  </p>
                </div>

                <aside className="cst-hero-aside-celestial">
                  <div className="cst-hero-stat-block">
                    <div className="cst-hero-stat-number">935</div>
                    <div className="cst-hero-stat-label">roles on our careers portal</div>
                    <button
                      type="button"
                      onClick={() => navigate('/careers')}
                      className="cst-hero-stat-link"
                    >
                      <span>Explore all opportunities</span>
                      <span className="cst-hero-stat-arrow">↗</span>
                    </button>
                    <div className="cst-hero-stat-sub">
                      Actively hiring globally across tech & AI
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </section>

          {/* SECTION 2: WORKSPACE (Search bar + Ecosystem Grid) */}
          <section className="cst-workspace-section">
            <div className="cst-workspace-container">
              {/* Full-width Compact Search Bar matching screenshot */}
              <form className="cst-searchbar-full" onSubmit={handleSearchSubmit}>
                <Search size={18} className="cst-searchbar-icon" />
                <input
                  type="text"
                  placeholder="Search by skills, location, role, experience... (Press Enter to add)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="cst-searchbar-input"
                  autoFocus
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => { setSearchQuery(''); setError(''); }}
                    className="cst-searchbar-clear-btn"
                    title="Clear search"
                  >
                    <X size={16} />
                  </button>
                )}
                <button type="submit" className="cst-searchbar-submit-btn" disabled={loading || !searchQuery.trim()}>
                  <span>Find opportunities</span>
                  <ArrowRight size={14} />
                </button>
              </form>

              {/* Sample Quick Tags */}
              <div className="cst-search-samples">
                <span className="cst-sample-label">Try sample:</span>
                <button type="button" className="cst-sample-chip" onClick={() => { setSearchQuery('CAN-000193'); fetchStatus('CAN-000193'); }}>
                  CAN-000193
                </button>
                <button type="button" className="cst-sample-chip" onClick={() => { setSearchQuery('CAN-000192'); fetchStatus('CAN-000192'); }}>
                  CAN-000192
                </button>
                <button type="button" className="cst-sample-chip" onClick={() => { setSearchQuery('erpraveenkumar2002@gmail.com'); fetchStatus('erpraveenkumar2002@gmail.com'); }}>
                  erpraveenkumar2002@gmail.com
                </button>
              </div>

              {/* Graceful Not Found Notification */}
              {error && (
                <div className="cst-not-found-banner">
                  <div className="cst-not-found-icon">
                    <AlertTriangle size={22} />
                  </div>
                  <div className="cst-not-found-body">
                    <strong>Application Record Not Found</strong>
                    <p>No active record was found matching <code>{searchQuery}</code>.</p>
                    <div className="cst-not-found-tips">
                      <span>• Check for typos in your Candidate ID (e.g. <code>CAN-000193</code>).</span>
                      <span>• Use the exact email address you submitted on the application form.</span>
                      <span>• If you recently applied, your profile is being processed and will be trackable shortly.</span>
                    </div>
                  </div>
                  <button type="button" onClick={handleClearSearch} className="cst-banner-reset-btn">
                    Reset
                  </button>
                </div>
              )}

              {/* Quick Action Navigation Cards Grid */}
              <div className="cst-portal-actions-grid">
                <div className="cst-portal-card" onClick={() => navigate('/join-our-bench')}>
                  <div className="cst-pcard-icon cst-icon-bench">
                    <Layers size={24} />
                  </div>
                  <h4>Join Our Bench</h4>
                  <p>Are you looking for immediate consulting projects? Join our exclusive talent bench for prioritized client submissions.</p>
                  <div className="cst-pcard-cta">
                    <span>Enroll on Bench</span>
                    <ArrowRight size={16} />
                  </div>
                </div>

                <div className="cst-portal-card" onClick={() => navigate('/careers')}>
                  <div className="cst-pcard-icon cst-icon-careers">
                    <Briefcase size={24} />
                  </div>
                  <h4>Browse Open Positions</h4>
                  <p>Explore current career openings across Fortune 500 clients, high-growth tech firms, and enterprise programs.</p>
                  <div className="cst-pcard-cta">
                    <span>View Careers</span>
                    <ArrowRight size={16} />
                  </div>
                </div>

                <div className="cst-portal-card" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                  <div className="cst-pcard-icon cst-icon-track">
                    <Sparkles size={24} />
                  </div>
                  <h4>5-Stage Milestone Tracking</h4>
                  <p>Full real-time visibility across Review, Client Presentation, Interview rounds, Offer Release, and Final Onboarding.</p>
                  <div className="cst-pcard-cta">
                    <span>Track Status Above</span>
                    <ChevronRight size={16} />
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Add / Edit Candidate Modal */}
      {isEditModalOpen && preparedCandidateForModal && (
        <AddModal
          candidate={preparedCandidateForModal}
          onClose={() => setIsEditModalOpen(false)}
          onSave={handleUpdateCandidate}
          uploading={saving}
        />
      )}
    </div>
  );
}
